import React, { useState, useEffect } from 'react';
import { Box, Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent, Tabs, Tab, Typography, Button, Table, TableBody, TableCell, TableHead, TableRow, Paper, Alert } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import JsonViewer from './JsonViewer';
import { fetchServiceStatuses } from '../api/serviceStatusApi';
import PipeHookTestButton from './PipeHookTestButton';

/**
 * ServiceStatusIndicator shows status "lights" for each cloud service
 * green/yellow/red indicators based on service health
 * 
 * @returns {JSX.Element} - ServiceStatusIndicator component
 */
const ServiceStatusIndicator = () => {
  // Status states for each service
  const [statuses, setStatuses] = useState({
    'sabal-cloud': { status: 'unknown' },
    'sabalsql-dbam': { status: 'unknown' },
    'debezium-server': { status: 'unknown' },
    'debezium-webhook': { status: 'unknown' },
    'someac': { status: 'ok' } // Always ok if UI is accessible
  });
  
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [dialogTab, setDialogTab] = useState(0);
  
  // Fetch service statuses from the API
  const fetchStatuses = async () => {
    try {
      setLoading(true);
      
      // Call the API to get real status data from the Cloud Function
      const data = await fetchServiceStatuses();
      
      console.log('Service status data:', data); // Debug log
      
      // Set the statuses from the API response with fallback
      const defaultStatuses = {
        'sabal-cloud': { status: 'unknown' },
        'sabalsql-dbam': { status: 'unknown' },
        'debezium-server': { status: 'unknown' },
        'debezium-webhook': { status: 'unknown' },
        'someac': { status: 'ok' }
      };
      
      // Safely merge the received services with defaults
      const receivedServices = data.services || {};
      const mergedServices = {};
      
      // Process each service
      ['sabal-cloud', 'sabalsql-dbam', 'debezium-server', 'debezium-webhook', 'someac'].forEach(serviceName => {
        mergedServices[serviceName] = {
          ...defaultStatuses[serviceName],
          ...(receivedServices[serviceName] || {})
        };
        
        // Ensure all services have essential properties to avoid rendering errors
        if (!mergedServices[serviceName].metrics) {
          mergedServices[serviceName].metrics = {
            errorRate: 0,
            uptime: '100%',
            responseTime: 'N/A'
          };
        }
      });
      
      setStatuses(mergedServices);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching service statuses:', error);
      setLoading(false);
    }
  };
  
  // Initial fetch and interval
  useEffect(() => {
    fetchStatuses();
    const interval = setInterval(fetchStatuses, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);
  
  // Get color for status
  const getStatusColor = (status) => {
    switch(status) {
      case 'ok': return '#4caf50'; // Green
      case 'warning': return '#ff9800'; // Yellow/Orange
      case 'error': return '#f44336'; // Red
      default: return '#9e9e9e'; // Grey for unknown
    }
  };
  
  // Handle opening detail dialog
  const handleOpenDialog = (service) => {
    try {
      setSelectedService(service);
      setOpenDialog(true);
      setDialogTab(0);
      
      console.log('Opening dialog for service:', service);
      console.log('Service data:', statuses[service]);
    } catch (error) {
      console.error('Error opening dialog:', error);
    }
  };
  
  // Handle dialog tab change
  const handleTabChange = (event, newValue) => {
    setDialogTab(newValue);
  };
  
  // Create a safe renderer that handles potential errors
  const safeSectionRenderer = (renderFn) => {
    try {
      return renderFn();
    } catch (error) {
      console.error('Error rendering section:', error);
      return (
        <Box sx={{ p: 2, color: 'error.main', display: 'flex', alignItems: 'center' }}>
          <ErrorOutlineIcon sx={{ mr: 1 }} />
          <Typography>Error rendering this section. Please check console for details.</Typography>
        </Box>
      );
    }
  };
  
  // Render service details dialog with error handling
  const renderServiceDetailsDialog = () => {
    if (!selectedService || !statuses[selectedService]) return null;
    
    try {
      const serviceData = statuses[selectedService] || { status: 'unknown' };
      
      return (
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {selectedService} Status
            <FiberManualRecordIcon 
              sx={{ 
                color: getStatusColor(serviceData.status),
                fontSize: 16,
                ml: 1,
                verticalAlign: 'middle'
              }} 
            />
          </Typography>
          <Button 
            startIcon={<RefreshIcon />} 
            onClick={fetchStatuses}
            size="small"
          >
            Refresh
          </Button>
        </DialogTitle>
        
        <Tabs value={dialogTab} onChange={handleTabChange} sx={{ px: 2 }}>
          <Tab label="Overview" />
          <Tab label="Metrics" />
          <Tab label="Recent Errors" />
          <Tab label="Health Details" />
          <Tab label="Service Data" />
        </Tabs>
        
        <DialogContent>
          {/* Overview Tab */}
          {dialogTab === 0 && safeSectionRenderer(() => {
            // Safely get properties with fallbacks
            const status = (serviceData.status || 'unknown').toUpperCase();
            const timestamp = serviceData.timestamp ? new Date(serviceData.timestamp).toLocaleString() : 'Unknown';
            const metrics = serviceData.metrics || {};
            const errorRate = metrics.errorRate !== undefined ? 
              (typeof metrics.errorRate === 'number' ? metrics.errorRate.toFixed(2) : metrics.errorRate) : '0.00';
            
            return (
              <Box>
                <Typography variant="subtitle1" gutterBottom>Service Status: {status}</Typography>
                <Typography variant="body2">Last Checked: {timestamp}</Typography>
                {serviceData.serviceUrl && (
                  <Typography variant="body2">Service URL: {serviceData.serviceUrl}</Typography>
                )}
                {serviceData.latestRevision && (
                  <Typography variant="body2">Latest Revision: {serviceData.latestRevision}</Typography>
                )}
                
                {/* Status Reason Alert */}
                {serviceData.reason && (
                  <Alert 
                    severity={serviceData.status === 'error' ? 'error' : 
                             serviceData.status === 'warning' ? 'warning' : 'info'} 
                    sx={{ mt: 2, mb: 2 }}
                  >
                    <Typography variant="body2">
                      <strong>Status Reason:</strong> {serviceData.reason}
                    </Typography>
                  </Alert>
                )}
                
                {/* Summary statistics */}
                <Paper sx={{ mt: 2, p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Summary</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2">Error Rate: {errorRate}%</Typography>
                      {metrics.totalLogs && (
                        <Typography variant="body2">Recent Logs: {metrics.totalLogs}</Typography>
                      )}
                      {metrics.httpCode && (
                        <Typography variant="body2">HTTP Status: {metrics.httpCode}</Typography>
                      )}
                    </Box>
                    <Box>
                      {metrics.mqttErrors !== undefined && (
                        <Typography variant="body2">MQTT Errors: {metrics.mqttErrors}</Typography>
                      )}
                      {metrics.dbErrors !== undefined && (
                        <Typography variant="body2">Database Errors: {metrics.dbErrors}</Typography>
                      )}
                      {metrics.websocketClients && (
                        <Typography variant="body2">WebSocket: {metrics.websocketClients}</Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Box>
            );
          })}
          
          {/* Metrics Tab */}
          {dialogTab === 1 && safeSectionRenderer(() => {
            const metrics = serviceData.metrics || {};
            
            return (
              <Box>
                <Typography variant="subtitle1" gutterBottom>Performance Metrics</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Metric</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Error Rate - always show this one with fallback */}
                    <TableRow>
                      <TableCell>Error Rate</TableCell>
                      <TableCell>
                        {metrics.errorRate !== undefined 
                          ? (typeof metrics.errorRate === 'number' 
                              ? metrics.errorRate.toFixed(2) 
                              : metrics.errorRate)
                          : '0.00'}%
                      </TableCell>
                      <TableCell>
                        <FiberManualRecordIcon 
                          sx={{ 
                            color: (metrics.errorRate || 0) > 5 ? '#f44336' : 
                                   (metrics.errorRate || 0) > 1 ? '#ff9800' : '#4caf50',
                            fontSize: 16
                          }} 
                        />
                      </TableCell>
                    </TableRow>
                    
                    {/* HTTP Status - always show */}
                    <TableRow>
                      <TableCell>HTTP Status</TableCell>
                      <TableCell>HTTP {metrics.httpCode || serviceData.httpCode || 'N/A'}</TableCell>
                      <TableCell>
                        <FiberManualRecordIcon 
                          sx={{ 
                            color: String(metrics.httpCode || serviceData.httpCode) === '200' ? '#4caf50' : '#f44336',
                            fontSize: 16
                          }} 
                        />
                      </TableCell>
                    </TableRow>
                    
                    {/* MQTT Errors - conditional */}
                    {metrics.mqttErrors !== undefined && (
                      <TableRow>
                        <TableCell>MQTT Connectivity</TableCell>
                        <TableCell>{metrics.mqttErrors} issues</TableCell>
                        <TableCell>
                          <FiberManualRecordIcon 
                            sx={{ 
                              color: metrics.mqttErrors > 3 ? '#f44336' : 
                                     metrics.mqttErrors > 0 ? '#ff9800' : '#4caf50',
                              fontSize: 16
                            }} 
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    
                    {/* DB Errors - conditional */}
                    {metrics.dbErrors !== undefined && (
                      <TableRow>
                        <TableCell>Database Connectivity</TableCell>
                        <TableCell>{metrics.dbErrors} issues</TableCell>
                        <TableCell>
                          <FiberManualRecordIcon 
                            sx={{ 
                              color: metrics.dbErrors > 3 ? '#f44336' : 
                                     metrics.dbErrors > 0 ? '#ff9800' : '#4caf50',
                              fontSize: 16
                            }} 
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    
                    {/* WebSocket Clients - conditional */}
                    {metrics.websocketClients && (
                      <TableRow>
                        <TableCell>WebSocket Status</TableCell>
                        <TableCell>{metrics.websocketClients}</TableCell>
                        <TableCell>
                          <FiberManualRecordIcon 
                            sx={{ 
                              color: metrics.websocketClients === 'connected' ? '#4caf50' : '#ff9800',
                              fontSize: 16
                            }} 
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    
                    {/* Min Instances - conditional */}
                    {metrics.minInstances !== undefined && (
                      <TableRow>
                        <TableCell>Minimum Instances</TableCell>
                        <TableCell>{metrics.minInstances}</TableCell>
                        <TableCell>
                          <FiberManualRecordIcon 
                            sx={{ 
                              color: parseInt(metrics.minInstances) >= 1 ? '#4caf50' : '#ff9800',
                              fontSize: 16
                            }} 
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    
                    {/* Uptime - show with fallback */}
                    <TableRow>
                      <TableCell>Uptime</TableCell>
                      <TableCell>{metrics.uptime || '100%'}</TableCell>
                      <TableCell>
                        <FiberManualRecordIcon 
                          sx={{ 
                            color: '#4caf50',
                            fontSize: 16
                          }} 
                        />
                      </TableCell>
                    </TableRow>
                    
                    {/* Response time - show with fallback */}
                    <TableRow>
                      <TableCell>Response Time</TableCell>
                      <TableCell>{metrics.responseTime || 'N/A'}</TableCell>
                      <TableCell>
                        <FiberManualRecordIcon 
                          sx={{ 
                            color: '#4caf50',
                            fontSize: 16
                          }} 
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            );
          })}
          
          {/* Recent Errors Tab */}
          {dialogTab === 2 && safeSectionRenderer(() => {
            const hasErrors = serviceData.recentErrors && Array.isArray(serviceData.recentErrors) && serviceData.recentErrors.length > 0;
            
            return (
              <Box>
                <Typography variant="subtitle1" gutterBottom>Recent Errors</Typography>
                {hasErrors ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {serviceData.recentErrors.map((error, index) => (
                        <TableRow key={index}>
                          <TableCell>{new Date(error.timestamp || new Date()).toLocaleString()}</TableCell>
                          <TableCell>{error.message || 'Unknown error'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2">No recent errors found. That's great!</Typography>
                )}
              </Box>
            );
          })}
          
          {/* Health Data Tab */}
          {dialogTab === 3 && safeSectionRenderer(() => {
            const actualData = serviceData.actualData;
            
            if (!actualData) {
              return (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>Health Data</Typography>
                  <Typography variant="body2" color="text.secondary">
                    No detailed health data available for this service.
                  </Typography>
                </Box>
              );
            }
            
            return (
              <Box>
                <Typography variant="subtitle1" gutterBottom>Service Health Details</Typography>
                
                {/* Basic Health Info */}
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Basic Health</Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {actualData.status && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Status</Typography>
                        <Typography variant="body1" sx={{ 
                          color: actualData.status === 'ok' ? 'success.main' : 'error.main',
                          fontWeight: 'bold'
                        }}>
                          {actualData.status.toUpperCase()}
                        </Typography>
                      </Box>
                    )}
                    {actualData.uptime && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Uptime</Typography>
                        <Typography variant="body1">
                          {Math.floor(actualData.uptime / 3600)}h {Math.floor((actualData.uptime % 3600) / 60)}m
                        </Typography>
                      </Box>
                    )}
                    {actualData.version && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Version</Typography>
                        <Typography variant="body1">{actualData.version}</Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
                
                {/* MQTT Health */}
                {actualData.health?.mqtt && (
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>MQTT Connectivity</Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Connection Status</TableCell>
                          <TableCell>
                            <FiberManualRecordIcon 
                              sx={{ 
                                color: actualData.health.mqtt.connected ? '#4caf50' : '#f44336',
                                fontSize: 16,
                                mr: 1
                              }} 
                            />
                            {actualData.health.mqtt.connected ? 'Connected' : 'Disconnected'}
                          </TableCell>
                        </TableRow>
                        {actualData.health.mqtt.lastConnection && (
                          <TableRow>
                            <TableCell>Last Connection</TableCell>
                            <TableCell>{new Date(actualData.health.mqtt.lastConnection).toLocaleString()}</TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell>Connection Attempts</TableCell>
                          <TableCell>{actualData.health.mqtt.connectionAttempts || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Successful Connections</TableCell>
                          <TableCell>{actualData.health.mqtt.successfulConnections || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Failed Connections</TableCell>
                          <TableCell>{actualData.health.mqtt.failedConnections || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Health Check Failures</TableCell>
                          <TableCell>{actualData.health.mqtt.healthCheckFailures || 0}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Paper>
                )}
                
                {/* Database Health */}
                {actualData.health?.database && (
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Database Connectivity</Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Circuit Breaker State</TableCell>
                          <TableCell>
                            <FiberManualRecordIcon 
                              sx={{ 
                                color: actualData.health.database.circuitBreakerState === 'CLOSED' ? '#4caf50' : '#f44336',
                                fontSize: 16,
                                mr: 1
                              }} 
                            />
                            {actualData.health.database.circuitBreakerState}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Circuit Breaker Failures</TableCell>
                          <TableCell>{actualData.health.database.circuitBreakerFailures || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Connection Attempts</TableCell>
                          <TableCell>{actualData.health.database.connectionAttempts || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Successful Connections</TableCell>
                          <TableCell>{actualData.health.database.successfulConnections || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Failed Connections</TableCell>
                          <TableCell>{actualData.health.database.failedConnections || 0}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Paper>
                )}
                
                {/* Processing Health */}
                {actualData.health?.processing && (
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Message Processing</Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Messages Received</TableCell>
                          <TableCell>{actualData.health.processing.messagesReceived || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Messages Processed</TableCell>
                          <TableCell>{actualData.health.processing.messagesProcessed || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Database Writes</TableCell>
                          <TableCell>{actualData.health.processing.databaseWrites || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Processing Errors</TableCell>
                          <TableCell>{actualData.health.processing.errors || 0}</TableCell>
                        </TableRow>
                        {actualData.health.processing.lastError && (
                          <TableRow>
                            <TableCell>Last Error</TableCell>
                            <TableCell sx={{ color: 'error.main' }}>
                              {actualData.health.processing.lastError}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                )}
                
                {/* Raw Health Data (collapsible) */}
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Raw Health Data</Typography>
                  <JsonViewer json={actualData} />
                </Paper>
              </Box>
            );
          })}
          
          {/* Raw Data Tab */}
          {dialogTab === 4 && safeSectionRenderer(() => {
            return (
              <Box>
                <Typography variant="subtitle1" gutterBottom>Service Status Details</Typography>
                
                {/* Service Metadata */}
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Service Information</Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Service Name</TableCell>
                        <TableCell>{serviceData.service || selectedService}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell>
                          <FiberManualRecordIcon 
                            sx={{ 
                              color: getStatusColor(serviceData.status),
                              fontSize: 16,
                              mr: 1
                            }} 
                          />
                          {serviceData.status?.toUpperCase() || 'UNKNOWN'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Last Checked</TableCell>
                        <TableCell>
                          {serviceData.timestamp ? new Date(serviceData.timestamp).toLocaleString() : 'Unknown'}
                        </TableCell>
                      </TableRow>
                      {serviceData.serviceUrl && (
                        <TableRow>
                          <TableCell>Service URL</TableCell>
                          <TableCell>{serviceData.serviceUrl}</TableCell>
                        </TableRow>
                      )}
                      {serviceData.latestRevision && (
                        <TableRow>
                          <TableCell>Latest Revision</TableCell>
                          <TableCell>{serviceData.latestRevision}</TableCell>
                        </TableRow>
                      )}
                      {serviceData.httpCode && (
                        <TableRow>
                          <TableCell>HTTP Status</TableCell>
                          <TableCell>HTTP {serviceData.httpCode}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>
                
                {/* Status Reason */}
                {serviceData.reason && (
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Status Reason</Typography>
                    <Alert 
                      severity={serviceData.status === 'error' ? 'error' : 
                               serviceData.status === 'warning' ? 'warning' : 'info'} 
                      sx={{ mb: 1 }}
                    >
                      {serviceData.reason}
                    </Alert>
                  </Paper>
                )}
                
                {/* Metrics Summary */}
                {serviceData.metrics && Object.keys(serviceData.metrics).length > 0 && (
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Performance Metrics</Typography>
                    <Table size="small">
                      <TableBody>
                        {Object.entries(serviceData.metrics).map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell sx={{ textTransform: 'capitalize' }}>
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </TableCell>
                            <TableCell>
                              {typeof value === 'number' && key.includes('Rate') ? `${value.toFixed(2)}%` : 
                               typeof value === 'number' && key.includes('Time') ? `${value}ms` :
                               String(value)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                )}
                
                {/* Revision Status (for Cloud Run services) */}
                {serviceData.revisionStatus && (
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Revision Status</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {serviceData.revisionStatus}
                    </Typography>
                  </Paper>
                )}
                
                {/* Complete Raw Data */}
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Complete Service Data</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    This is the complete data structure returned by the service status API:
                  </Typography>
                  <JsonViewer json={serviceData} />
                </Paper>
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>
      );
    } catch (error) {
      console.error('Error rendering dialog:', error);
      return (
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)}
        >
          <DialogTitle sx={{ color: 'error.main' }}>Error Displaying Service Details</DialogTitle>
          <DialogContent>
            <Typography>
              There was an error displaying the service details. This may be due to unexpected data format.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Error details: {error.message}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={() => setOpenDialog(false)}
              >
                Close
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      );
    }
  };
  
  // Loading state
  if (loading && Object.values(statuses).every(s => s.status === 'unknown')) {
    return <CircularProgress size={16} />;
  }
  
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
        {['sabal-cloud', 'sabalsql-dbam', 'debezium-server', 'debezium-webhook', 'someac'].map(service => (
          <Tooltip 
            key={service}
            title={`${service}: ${statuses[service]?.status?.toUpperCase() || 'UNKNOWN'} (Click for details)`}
          >
            <FiberManualRecordIcon 
              onClick={() => handleOpenDialog(service)}
              sx={{ 
                color: getStatusColor(statuses[service]?.status),
                fontSize: 16,
                mx: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                  transform: 'scale(1.2)'
                }
              }} 
            />
          </Tooltip>
        ))}
        
        {/* Pipe Hook Test Button */}
        <Box sx={{ ml: 1, mr: 0.5 }}>
          <PipeHookTestButton />
        </Box>
      </Box>
      
      {/* Service Details Dialog */}
      {renderServiceDetailsDialog()}
    </>
  );
};

export default ServiceStatusIndicator;
